import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Ban, Check, Package } from 'lucide-react';
import {
  type RequisitionApprovalInput,
  type RequisitionLineDto,
} from '@eombor/contracts';
import { Badge, Button, Dialog, Input, Spinner, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { stockApi } from '../../stock/api/stock.api.js';
import { stockKeys } from '../../stock/model/stock.queries.js';
import { demandMapOf, usePendingDemand } from '../model/requisition.queries.js';

/**
 * KORXONA BOSHLIG'INING TASDIQ OYNASI.
 *
 * Nega u bor: "10 ta so'radi, men 5 ta beraman" — bu boshliqning qarori va
 * hujjatning eng muhim qadamlaridan biri. Ilgari bu qadamda satr ma'lumoti
 * UMUMAN yo'q edi: boshliq faqat butun talabnomani tasdiqlar yoki rad
 * etardi, miqdorni esa keyinroq skladchi kesardi. Ya'ni boshliqning qarori
 * skladchining qaroriga aylanib qolardi va hujjatda kimning qarori ekani
 * bilinmasdi.
 *
 * Ikki qaror bir-birini ALMASHTIRMAYDI: boshliq HUQUQNI beradi ("shunchasiga
 * haqlisiz"), skladchi esa MAVJUDLIKNI aytadi ("shunchasi bor"). Shuning
 * uchun ikki ustun va shuning uchun ikki oyna.
 *
 * Nol — haqiqiy qaror: boshliq butun talabnomani rad etmasdan bitta satrni
 * kesib tashlay oladi. Aks holda bitta ortiqcha satr uchun butun hujjat
 * qaytarilardi va odam hammasini qaytadan yozardi.
 */

export interface OrgApproveDialogProps {
  open: boolean;
  onClose: () => void;
  /** Talabnoma qaysi omborga yozilgan — qoldiq va talab shu ombordan. */
  warehouseId: string;
  /** Shu talabnomaning o'zi talab sanog'idan chiqariladi. */
  requisitionId: string;
  lines: readonly RequisitionLineDto[];
  /** Nomenklatura nomi/kodi — kartochkada allaqachon yuklangan ma'lumotnoma. */
  nameOf: (nomenclatureId: string) => { name: string; code: string; unit?: string } | undefined;
  onConfirm: (approvals: RequisitionApprovalInput[]) => void;
  pending?: boolean;
  error?: string | null;
}

const qtyText = (qty: number): string => String(Math.round(qty * 1000) / 1000);

/** Satr bo'yicha qaror — miqdor MATN, chunki odam uni yozib turadi. */
interface Decision {
  qty: string;
  note: string;
}

/** Kiritilgan miqdor — o'qib bo'lmasa `null`. */
const parse = (raw: string): number | null => {
  const value = Number(raw.replace(',', '.').trim());
  return raw.trim() === '' || Number.isNaN(value) || value < 0 ? null : value;
};

export const OrgApproveDialog = ({
  open,
  onClose,
  warehouseId,
  requisitionId,
  lines,
  nameOf,
  onConfirm,
  pending = false,
  error = null,
}: OrgApproveDialogProps) => {
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});

  /*
   * OMBORDA QANCHA BOR — har satr uchun alohida so'rov, aniq filtr bilan.
   *
   * `AcceptDialog` bilan bir xil naqsh va bir xil sabab: butun ombor
   * qoldig'ini tortib olib mijozda filtrlash haqiqiy omborda minglab
   * qatorni behuda olib kelardi va sahifalash chegarasiga urilib, kerakli
   * mahsulot ro'yxatga tushmay qolishi mumkin edi.
   */
  const stock = useQueries({
    queries: lines.map((line) => ({
      queryKey: stockKeys.search({
        warehouseId,
        nomenclatureId: line.nomenclatureId,
        onlyAvailable: true,
      }),
      queryFn: () =>
        stockApi.search({
          warehouseId,
          nomenclatureId: line.nomenclatureId,
          onlyAvailable: true,
        }),
      enabled: open,
    })),
  });

  const availableOf = useMemo(() => {
    const totals = new Map<string, number>();
    lines.forEach((line, index) => {
      const group = stock[index]?.data?.items[0];
      totals.set(line.nomenclatureId, group?.totalAvailableQty ?? 0);
    });
    return totals;
  }, [lines, stock]);

  /* Kutilayotgan talab — SHU hujjatsiz. Boshliq "boshqa talabnomalarda
     qancha so'ralgan" degan savolga javob ko'radi; o'zi ko'rib turgan
     hujjat ham sanalsa, son o'z-o'zini hisobga olib qo'rqitardi. */
  const demand = usePendingDemand(
    open ? lines.map((line) => line.nomenclatureId) : [],
    warehouseId,
    requisitionId,
  );
  const demandMap = useMemo(() => demandMapOf(demand.data ?? []), [demand.data]);
  const loading = stock.some((query) => query.isPending);

  /* Har ochilishda SO'RALGAN miqdordan boshlanadi.
     Odatiy qaror — "hammasini beraman", shuning uchun boshliq faqat
     KESMOQCHI bo'lgan satrga tegadi. Bo'sh maydonlardan boshlansa, u
     o'zgartirmoqchi bo'lmagan satrlarni ham qayta yozib chiqardi. */
  useEffect(() => {
    if (!open) return;
    const next: Record<string, Decision> = {};
    for (const line of lines) {
      next[line.id] = { qty: qtyText(line.orgApprovedQty ?? line.requestedQty), note: '' };
    }
    setDecisions(next);
  }, [open, lines]);

  const grantAll = (): void => {
    const next: Record<string, Decision> = {};
    for (const line of lines) next[line.id] = { qty: qtyText(line.requestedQty), note: '' };
    setDecisions(next);
  };

  const patch = (lineId: string, value: Partial<Decision>): void => {
    setDecisions((prev) => ({
      ...prev,
      [lineId]: { qty: '', note: '', ...prev[lineId], ...value },
    }));
  };

  const approvals: RequisitionApprovalInput[] = lines.flatMap((line) => {
    const decision = decisions[line.id];
    const qty = decision === undefined ? null : parse(decision.qty);
    if (qty === null) return [];
    const note = decision?.note.trim() ?? '';
    return [{ lineId: line.id, approvedQty: qty, ...(note === '' ? {} : { note })
    }];
  });

  const invalid = lines.some((line) => {
    const decision = decisions[line.id];
    const qty = decision === undefined ? null : parse(decision.qty);
    return qty === null || qty > line.requestedQty;
  });
  /* Hammasi nol — bu tasdiq emas, RAD ETISH. Uni tasdiq tugmasi ostida
     yashirish hujjatni "tasdiqlangan, lekin hech nima berilmaydi" degan
     ma'nosiz holatga olib kelardi. */
  const nothingGranted = approvals.length > 0 && approvals.every((a) => a.approvedQty <= 0);
  const cut = approvals.filter((approval) => {
    const line = lines.find((l) => l.id === approval.lineId);
    return line !== undefined && approval.approvedQty < line.requestedQty;
  }).length;

  return (
    <Dialog
      open={open}
      size="lg"
      onClose={onClose}
      title={t('requisition.orgApproveTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('requisition.cancel')}
          </Button>
          <Button variant="secondary" onClick={grantAll}>
            {t('requisition.orgGrantAll')}
          </Button>
          <Button
            loading={pending}
            disabled={invalid || nothingGranted}
            onClick={() => onConfirm(approvals)}
          >
            <Check size={14} aria-hidden />
            {/* Oynani OCHGAN tugma bilan bir xil matn bo'lmasligi kerak:
                sarlavha allaqachon "Korxona tasdig'i" deb turibdi va ikki
                bir xil tugma qaysi biri oxirgi qadam ekanini yo'qotardi. */}
            {t('requisition.orgApproveConfirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm leading-6 text-text-muted">{t('requisition.orgApproveHint')}</p>

        {loading ? (
          <p className="flex items-center gap-2 text-sm text-text-muted">
            <Spinner size="sm" />
            {t('requisition.acceptLoading')}
          </p>
        ) : null}

        <ul className="flex flex-col gap-3">
          {lines.map((line) => {
            const info = nameOf(line.nomenclatureId);
            const decision = decisions[line.id];
            const qty = decision === undefined ? null : parse(decision.qty);
            const denied = qty === 0;
            const tooMuch = qty !== null && qty > line.requestedQty;
            const available = availableOf.get(line.nomenclatureId) ?? 0;
            const pendingQty = demandMap.get(`${warehouseId}:${line.nomenclatureId}`) ?? 0;
            /* Erkin qoldiq manfiy bo'lishi MUMKIN va u nolga
               qisqartirilmaydi: "3 ta yetmayapti" degani va uni yashirish
               aynan qaror qabul qiladigan odamdan yashirish bo'lardi. */
            const free = available - pendingQty;
            const unit = info?.unit === undefined ? '' : unitText(info.unit as never);

            return (
              <li
                key={line.id}
                className={cn(
                  'flex flex-col gap-3 rounded-[var(--radius-md)] border p-3',
                  denied ? 'border-danger/25 bg-danger-soft' : 'border-border bg-surface-2',
                )}
              >
                <div className="flex items-start gap-2.5">
                  <span
                    aria-hidden
                    className={cn(
                      'flex size-8 shrink-0 items-center justify-center',
                      'rounded-[var(--radius-sm)]',
                      denied ? 'bg-danger/10 text-danger' : 'bg-accent-soft text-accent',
                    )}
                  >
                    {denied ? <Ban size={15} /> : <Package size={15} />}
                  </span>

                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-sm leading-snug font-medium text-balance text-text">
                      {info?.name ?? `#${line.nomenclatureId}`}
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="tabular text-xs text-text-faint">{info?.code ?? ''}</span>
                      {/* SO'RALGAN miqdor doim ko'rinadi: boshliq nimani
                          kesayotganini bilmasdan kesa olmaydi. */}
                      <span className="tabular text-xs text-text-muted">
                        {t('requisition.orgRequested', {
                          qty: qtyText(line.requestedQty),
                          unit,
                        })}
                      </span>
                      {denied ? <Badge tone="danger">{t('requisition.orgNotGranted')}</Badge> : null}
                    </span>
                  </span>
                </div>

                {/*
                  OMBORDA / KUTILMOQDA / ERKIN — boshliq qaror qilish uchun
                  ko'rishi kerak bo'lgan uchlik.
                  Mavjud son YOLG'IZ turganda yolg'on gapiradi: u faqat
                  qattiq bronni hisobga oladi, bron esa skladchi qabul
                  qilgandagina qo'yiladi. Boshqa stansiyaning so'rovi undan
                  oldin sonni umuman o'zgartirmaydi.
                */}
                <dl className="grid grid-cols-3 gap-2 text-center">
                  <Figure label={t('requisition.orgInStock')} value={qtyText(available)} />
                  <Figure
                    label={t('requisition.orgPending')}
                    value={qtyText(pendingQty)}
                    tone={pendingQty > 0 ? 'warn' : undefined}
                  />
                  <Figure
                    label={t('requisition.orgFree')}
                    value={qtyText(free)}
                    tone={free <= 0 ? 'danger' : free < line.requestedQty ? 'warn' : undefined}
                  />
                </dl>

                {/* So'ralgani erkin qoldiqdan ko'p — boshliq buni
                    TASDIQLASHDAN OLDIN bilishi kerak. */}
                {qty !== null && qty > free ? (
                  <p className={cn('text-xs', free <= 0 ? 'text-danger' : 'text-warn')}>
                    {t('requisition.orgShort', { qty: qtyText(qty - free) })}
                  </p>
                ) : null}

                <div className="grid gap-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
                  <Input
                    mono
                    label={t('requisition.orgGrantQty')}
                    inputMode="decimal"
                    autoComplete="off"
                    value={decision?.qty ?? ''}
                    onChange={(event) => patch(line.id, { qty: event.target.value })}
                    suffix={unit}
                    error={
                      qty === null
                        ? t('requisition.acceptQtyInvalid')
                        : tooMuch
                          ? t('requisition.orgTooMuch')
                          : undefined
                    }
                  />
                  {/* Nega kesilgani — buyurtmachi shu izohni o'qiydi va
                      "nega 10 emas?" deb telefon qilmaydi. */}
                  <Input
                    label={t('requisition.orgNote')}
                    autoComplete="off"
                    maxLength={500}
                    value={decision?.note ?? ''}
                    onChange={(event) => patch(line.id, { note: event.target.value })}
                  />
                </div>
              </li>
            );
          })}
        </ul>

        {nothingGranted ? (
          <p role="alert" className="text-sm text-danger">
            {t('requisition.orgNoneGranted')}
          </p>
        ) : cut > 0 ? (
          <p className="text-sm text-warn">{t('requisition.orgCutCount', { count: cut })}</p>
        ) : null}

        {error === null ? null : (
          <p
            role="alert"
            className={cn(
              'rounded-[var(--radius-sm)] border border-danger/25 bg-danger-soft',
              'px-3 py-2 text-sm text-danger',
            )}
          >
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
};

/**
 * Bitta son va uning yorlig'i.
 *
 * Uchala son BIR XIL ko'rinishda turadi: ular bir-biri bilan solishtiriladi
 * va turli o'lchamdagi raqamlar solishtirishni qiyinlashtirardi.
 */
const Figure = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'warn' | 'danger';
}) => (
  <div className="flex flex-col gap-0.5 rounded-[var(--radius-sm)] bg-surface px-2 py-1.5">
    <dt className="text-[0.6875rem] tracking-wide text-text-faint uppercase">{label}</dt>
    <dd
      className={cn(
        'tabular text-sm font-semibold',
        tone === 'danger' ? 'text-danger' : tone === 'warn' ? 'text-warn' : 'text-text',
      )}
    >
      {value}
    </dd>
  </div>
);
